import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiRefreshCw } from "react-icons/fi";
import { FaRegFilePdf, FaFileImage } from "react-icons/fa";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot
} from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/ResourcesPanel.css";
import "./staffcss/peerchats.css";

const CATEGORIES = [
  "Menstrual Wellness and Hygiene",
  "Contraception",
  "Fertility Basics",
  "STI Awareness",
  "Mental Health in Reproductive Care",
  "Male Reproductive Health",
  "Pregnancy Prevention",
  "Gender-Based Violence",
  "General Reproductive Wellness"
];

const PeerResources = () => {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const resourcesPerPage = 10;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch resources function
  const fetchResources = () => {
    setIsRefreshing(true);
    setLoading(true);
    const q = query(
      collection(db, "resources"),
      where("status", "!=", "Archived"),
      orderBy("createdAt", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const resourcesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setResources(resourcesData);
      setIsRefreshing(false);
      setLoading(false);
    });
    return unsubscribe;
  };

  // Fetch resources from resources collection (only Active status)
  useEffect(() => {
    const unsubscribe = fetchResources();
    return unsubscribe;
  }, [refreshKey]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter]);

  // Filter resources
  const filteredResources = resources.filter(r => {
    const matchesSearch = 
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.category.toLowerCase().includes(search.toLowerCase());
    
    const matchesCategory = 
      categoryFilter === "" || r.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const indexOfLastResource = currentPage * resourcesPerPage;
  const indexOfFirstResource = indexOfLastResource - resourcesPerPage;
  const currentResources = filteredResources.slice(indexOfFirstResource, indexOfLastResource);
  const totalPages = Math.ceil(filteredResources.length / resourcesPerPage);

  // Format date
  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleDateString();
    }
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }
    return new Date(dateValue).toLocaleDateString();
  };

  return (
    <div className="ma-main review-appointments-panel">
      {/* Header */}
      <div className="content-library-header-custom" style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Peer Educator - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Peer Educator
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            margin: 0,
            fontSize: '36px',
            fontWeight: '800',
            color: '#ffffff !important',
            background: 'none !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: '#ffffff !important',
            backgroundClip: 'unset !important',
            letterSpacing: '-0.02em',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Peer Resources
          </h2>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: '18px',
            color: '#ffffff',
            fontWeight: '500',
            textShadow: '0 1px 4px rgba(0,0,0,0.15)'
          }}>
            Educational resources and materials for peer education
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          .content-library-header-custom h2 {
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: #ffffff !important;
            background-clip: unset !important;
          }
          .content-library-header-custom p {
            color: #ffffff !important;
          }
        `}</style>
      </div>

      {/* Search and Controls */}
      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input
            className="admin-aura-search"
            style={{ paddingLeft: 36, width: '100%' }}
            placeholder="Search resources..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="admin-aura-select"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(category => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        <button
          onClick={() => {
            setIsRefreshing(true);
            setRefreshKey(k => k + 1);
          }}
          disabled={isRefreshing}
          className="admin-primary-gradient-btn"
          style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
        >
          <FiRefreshCw className={isRefreshing ? "spinning" : ""} /> {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Pagination */}
      <div style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "16px",
        alignItems: "center",
        marginTop: "16px"
      }}>
        <div style={{ fontSize: "14px", color: "#6b7280" }}>
          {filteredResources.length > 0 ? (
            `${indexOfFirstResource + 1}-${Math.min(indexOfLastResource, filteredResources.length)} of ${filteredResources.length}`
          ) : (
            '0 of 0'
          )}
        </div>

        {/* Pagination Arrows */}
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              backgroundColor: '#1e3a8a',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '32px'
            }}
          >
            ←
          </button>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            style={{
              backgroundColor: '#1e3a8a',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '32px'
            }}
          >
            →
          </button>
        </div>
      </div>

      {/* Resources Table */}
      <div className="inquiries-table" style={{ marginTop: '24px' }}>
        <div className="table-header" style={{ gridTemplateColumns: '3fr 2fr 1fr' }}>
          <div>Title</div>
          <div>Category</div>
          <div>Date Added</div>
        </div>
        {loading ? (
          <>
            {[...Array(10)].map((_, i) => (
              <div 
                key={i} 
                className="table-row admin-aura-skeleton-row"
                style={{ gridTemplateColumns: '3fr 1.5fr 1fr' }}
              >
                <div>
                  <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} />
                </div>
                <div>
                  <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${45 + (i % 4) * 10}%` }} />
                </div>
                <div>
                  <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} />
                </div>
              </div>
            ))}
          </>
        ) : currentResources.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
            No resources found.
          </div>
        ) : (
          currentResources.map(resource => (
            <div 
              key={resource.id} 
              className="table-row"
              style={{ gridTemplateColumns: '3fr 1.5fr 1fr', cursor: 'pointer' }}
              onClick={() => navigate(`/resource-view/${resource.id}`)}
            >
              <div>
                <div className="rp-title-cell" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {resource.type === 'pdf' ? <FaRegFilePdf /> : <FaFileImage />}
                  <span>{resource.title}</span>
                </div>
              </div>
              <div><span className="rp-category">{resource.category}</span></div>
              <div>{formatDate(resource.createdAt)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PeerResources;
